import { EnumOptionsType, WidgetProps } from '@rjsf/utils';
import { Badge, Box, ColumnLayout, TextContent } from '@amzn/awsui-components-react-v3';
import React from 'react';
import { diffWords } from 'diff';
import {
  colorBackgroundDropdownItemHover,
  colorChartsGreen300,
  colorChartsGreen400,
  colorChartsRed300,
  colorChartsRed400,
} from '@amzn/awsui-design-tokens-console';

export const UNIT_SEPARATOR = '\u001F';

export interface DiffWidgetProps extends WidgetProps {}

const getDisplayValue = (value: string, enumOptions?: EnumOptionsType[]): string => {
  if (!enumOptions || !value) return value;
  const values = value.split(', ');
  const labels = values.map((v) => {
    const option = enumOptions.find((opt) => String(opt.value) === v);
    return option?.label ?? v;
  });
  return labels.join(', ');
};

const renderWordDiff = (oldStr: string, newStr: string, showRemoved: boolean) => {
  const changes = diffWords(oldStr, newStr);
  return changes.map((part, i) => {
    if (part.added && showRemoved) return null;
    if (part.removed && !showRemoved) return null;
    const highlight = showRemoved ? part.removed : part.added;
    return (
      <span
        key={i}
        style={{
          backgroundColor: highlight ? (showRemoved ? colorChartsRed400 : colorChartsGreen400) : 'transparent',
        }}
      >
        {part.value}
      </span>
    );
  });
};

/**
 * Diff widget that displays before/after comparison of values.
 *
 * Features:
 * - Expects value format: "previous{UNIT_SEPARATOR}current"
 * - Shows side-by-side comparison when values differ with word-level highlighting
 * - Shows single value with "No change" badge when values are identical
 * - Returns empty fragment if no value provided
 *
 * @param props.value - Encoded string containing previous and current values
 * @returns Visual diff comparison or empty fragment
 */
export function DiffWidget(props: DiffWidgetProps) {
  let value = props.value;
  if (!props.value || (typeof props.value === 'object' && Object.keys(props.value).length === 0)) {
    if (props.schema.type === 'boolean') {
      // boolean values with undefined value default to false
      value = 'false' + UNIT_SEPARATOR + 'false';
    } else if (props.schema.type === 'string') {
      // Field has no data on either side — skip rendering entirely.
      // Without this, empty conditional fields (e.g. s3_info when S3 wasn't selected)
      // would show up as an empty "No change" badge.
      return <></>;
    } else {
      return <>{props.label}</>;
    }
  }

  if (props.schema.type === 'array' && typeof props.value === 'object') {
    // for multi-value field, merge each diff element
    const befores: string[] = [];
    const afters: string[] = [];

    Object.values(props.value).forEach((v) => {
      if (typeof v === 'string') {
        const [before, after] = v.split(UNIT_SEPARATOR);
        if (before) befores.push(before);
        if (after) afters.push(after);
      }
    });

    value = befores.join(', ') + UNIT_SEPARATOR + afters.join(', ');
  }

  if (typeof value !== 'string') return <>{value}</>;

  const [previous, current] = value.split(UNIT_SEPARATOR);
  const enumOptions = props.options?.enumOptions;
  const displayPrevious = getDisplayValue(previous, enumOptions);
  const displayCurrent = getDisplayValue(current, enumOptions);

  const hasChanged = previous !== current;

  return (
    <Box>
      {hasChanged ? (
        <ColumnLayout columns={2}>
          <Box>
            <TextContent>
              <div
                style={{ backgroundColor: colorChartsRed300, padding: '8px', borderRadius: '4px', minHeight: '1em' }}
              >
                {displayPrevious ? renderWordDiff(displayPrevious, displayCurrent, true) : '\u00A0'}
              </div>
            </TextContent>
          </Box>
          <Box>
            <TextContent>
              <div
                style={{ backgroundColor: colorChartsGreen300, padding: '8px', borderRadius: '4px', minHeight: '1em' }}
              >
                {displayCurrent ? renderWordDiff(displayPrevious, displayCurrent, false) : '\u00A0'}
              </div>
            </TextContent>
          </Box>
        </ColumnLayout>
      ) : (
        <Box>
          <TextContent>
            <div style={{ padding: '8px', backgroundColor: colorBackgroundDropdownItemHover, borderRadius: '4px' }}>
              {displayCurrent} <Badge color='grey'>No change</Badge>
            </div>
          </TextContent>
        </Box>
      )}
    </Box>
  );
}
